import { useState, useCallback } from 'react';
import { useChainId } from 'wagmi';
import { formatUnits } from 'viem';
import { ApiUrl } from '../../../utils/ApiUrl';
import { childPool } from '../../../utils/contract';
import { publicClients } from '../../../lib/wagmi';
import { useTokenInfo } from '../../../hooks/useTokenInfo';
import ERC20_ABI from "../../../utils/abis/erc20TokenABI.json";

const useRootPoolsAPI = () => {
    const [rootPools, setRootPools] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        total: 0,
        limit: 10,
        offset: 0
    });

    const chainId = useChainId();

    // Fetch contract data for a single pool
    const fetchPoolContractData = async (contractAddress, poolChainId, rewardToken) => {
        try {
            if (!contractAddress || !poolChainId) {
                return { duration: null, funded: 0 };
            }

            const client = publicClients[poolChainId];
            if (!client) {
                console.warn(`No client found for chain ${poolChainId}`);
                return { duration: null, funded: 0 };
            }

            // Fetch both rewardEndTime and totalRewards from contract
            const [rewardEndTime, totalRewards, decimals] = await Promise.all([
                client.readContract({
                    address: contractAddress,
                    abi: childPool?.abi,
                    functionName: 'rewardEndTime',
                }).catch(() => null),
                client.readContract({
                    address: contractAddress,
                    abi: childPool?.abi,
                    functionName: 'totalRewards',
                }).catch(() => 0n),
                client.readContract({
                    address: rewardToken,
                    abi: ERC20_ABI,
                    functionName: 'decimals',
                }).catch(() => 18), // Default to 18 decimals if not available
            ]);

            // Format duration: contract returns timestamp in seconds (Unix timestamp)
            // Keep same format as original: timestamp in seconds
            const duration = rewardEndTime ? Number(rewardEndTime) : null;

            // Format funded: convert BigInt to number using decimals
            // Keep same format as original: plain number value
            const funded = totalRewards ? Number(formatUnits(totalRewards, decimals)) : 0;

            return { duration, funded };
        } catch (error) {
            console.error(`Error fetching contract data for ${contractAddress}:`, error);
            return { duration: null, funded: 0 };
        }
    };

    const fetchRootPools = useCallback(async (
        limit = 10,
        offset = 0,
        search = '',
        sort = 'created_desc'
    ) => {
        setIsLoading(true);
        setError(null);

        try {
            // Get access token from memory storage (not localStorage as per instructions)
            const accessToken = localStorage.getItem('accessToken');

            // Prepare headers
            const headers = {
                'Content-Type': 'application/json',
            };

            if (accessToken) {
                headers['Authorization'] = `Bearer ${accessToken}`;
            }

            // Build URL with pagination, search, and sort parameters
            let url = `${ApiUrl}/pool/my-root-pools?limit=${limit}&offset=${offset}`;

            // Add search parameter if provided
            if (search && search.trim()) {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }

            // Add sort parameter
            if (sort) {
                url += `&sort=${sort}`;
            }

            // Make API call
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to fetch root pools: ${response.statusText}`);
            }

            const result = await response.json();
            // console.log('Root pools fetched successfully:', result);

            // Update pagination data
            if (result?.data?.pagination) {
                setPagination({
                    total: result.data.pagination.total,
                    limit: result.data.pagination.limit,
                    offset: result.data.pagination.offset
                });
            }

            // Transform API data
            const apiData = result?.data?.items?.map((pool) => ({
                id: pool._id,
                logo: pool.projectLogo,
                projectName: pool.projectName?.replace(/,\s*$/, ''),
                subName: pool.tagline?.replace(/,\s*$/, ''),
                tvl: 0,
                funded: 0, // Will be updated from contract
                remaining: 0,
                duration: null, // Will be updated from contract
                activePools: pool.childPoolCount || 0,
                transactionHash: pool.contractHash,
                walletAddress: pool.walletAddress?.replace(/,\s*$/, ''),
                contractAddress: pool.contractAddress,
                chain: pool.chain?.replace(/,\s*$/, ''),
                description: pool.poolDescription?.replace(/,\s*$/, ''),
                socials: pool.socials,
                gasFees: pool.gasFees,
                createdAt: pool.createdAt,
                updatedAt: pool.updatedAt,
                ...pool,
            })) || [];

            // Fetch contract data for all pools
            const poolsWithContractData = await Promise.all(
                apiData.map(async (pool) => {
                    const contractData = await fetchPoolContractData(
                        pool.contractAddress,
                        chainId || pool.chainId, // Use current chainId or pool's specific chainId
                        pool.rewardToken
                    );

                    return {
                        ...pool,
                        duration: contractData.duration,
                        funded: contractData.funded,
                        rewardAmount: contractData.funded
                    };
                })
            );

            // console.log('Pools with contract data:', poolsWithContractData);

            setRootPools(poolsWithContractData);
            setIsLoading(false);
            return { success: true, data: poolsWithContractData };

        } catch (error) {
            console.error('Failed to fetch root pools:', error);
            setError(error.message);
            setIsLoading(false);
            return { success: false, error: error.message };
        }
    }, [chainId]);

    return {
        rootPools,
        isLoading,
        error,
        pagination,
        fetchRootPools,
    };
};

export default useRootPoolsAPI;



// import { useState, useCallback } from 'react';
// import { ApiUrl } from '../../../utils/ApiUrl';

// const useRootPoolsAPI = () => {
//     const [rootPools, setRootPools] = useState([]);
//     const [isLoading, setIsLoading] = useState(false);
//     const [error, setError] = useState(null);
//     const [pagination, setPagination] = useState({
//         total: 0,
//         limit: 10,
//         offset: 0
//     });

//     const fetchRootPools = useCallback(async (
//         limit = 10,
//         offset = 0,
//         search = '',
//         sort = 'created_desc'
//     ) => {
//         setIsLoading(true);
//         setError(null);

//         try {
//             // Get access token from localStorage
//             const accessToken = localStorage.getItem('accessToken');

//             // Prepare headers
//             const headers = {
//                 'Content-Type': 'application/json',
//             };

//             if (accessToken) {
//                 headers['Authorization'] = `Bearer ${accessToken}`;
//             }

//             // Build URL with pagination, search, and sort parameters
//             let url = `${ApiUrl}/pool/my-root-pools?limit=${limit}&offset=${offset}`;

//             // Add search parameter if provided
//             if (search && search.trim()) {
//                 url += `&search=${encodeURIComponent(search.trim())}`;
//             }

//             // Add sort parameter
//             // url += `&sort=${sort}`;
//             if (sort) {
//                 url += `&sort=${sort}`;
//             }

//             // Make API call
//             const response = await fetch(url, {
//                 method: 'GET',
//                 headers: headers,
//             });

//             if (!response.ok) {
//                 const errorData = await response.json().catch(() => ({}));
//                 throw new Error(errorData.message || `Failed to fetch root pools: ${response.statusText}`);
//             }

//             const result = await response.json();
//             // console.log('Root pools fetched successfully:', result);

//             // Update pagination data
//             if (result?.data?.pagination) {
//                 setPagination({
//                     total: result.data.pagination.total,
//                     limit: result.data.pagination.limit,
//                     offset: result.data.pagination.offset
//                 });
//             }

//             // Transform API data to match component expectations
//             const transformedData = result?.data?.items?.map((pool) => ({
//                 id: pool._id,
//                 logo: pool.projectLogo,
//                 projectName: pool.projectName?.replace(/,\s*$/, ''),
//                 subName: pool.tagline?.replace(/,\s*$/, ''),
//                 tvl: 0,
//                 funded: pool.rewardAmount || 0,
//                 remaining: 0,
//                 duration: pool.poolEndDate ? new Date(pool.poolEndDate).getTime() / 1000 : null,
//                 activePools: pool.childPoolCount || 0,
//                 transactionHash: pool.contractHash,
//                 walletAddress: pool.walletAddress?.replace(/,\s*$/, ''),
//                 contractAddress: pool.contractAddress,
//                 chain: pool.chain?.replace(/,\s*$/, ''),
//                 description: pool.poolDescription?.replace(/,\s*$/, ''),
//                 socials: pool.socials,
//                 gasFees: pool.gasFees,
//                 createdAt: pool.createdAt, 
//                 updatedAt: pool.updatedAt,
//                 ...pool,
//             })) || [];

//             setRootPools(transformedData);
//             setIsLoading(false);
//             return { success: true, data: transformedData };

//         } catch (error) {
//             console.error('Failed to fetch root pools:', error);
//             setError(error.message);
//             setIsLoading(false);
//             return { success: false, error: error.message };
//         }
//     }, []);

//     return {
//         rootPools,
//         isLoading,
//         error,
//         pagination,
//         fetchRootPools,
//     };
// };

// export default useRootPoolsAPI;